import { useQuery } from "@tanstack/react-query";

import { logisticsApi } from "@/lib/logistics/logistics-api";
import { supportApi } from "@/lib/support/support-api";

/**
 * Compteurs de la barre latérale.
 *
 * Ils étaient écrits en dur — « Catalogue 2.4k », « Logistique 38 »,
 * « Support CSR 23 » — et ne bougeaient jamais. Un chef de hub voyait trente-
 * huit colis à préparer alors que la file était vide, et le support affichait
 * vingt-trois tickets pour trois réellement ouverts. Un compteur faux est pire
 * qu'aucun compteur : on cesse d'y croire, puis on cesse de les regarder.
 *
 * Ne sont conservés que les compteurs qui appellent une ACTION — des colis à
 * préparer, des tickets à traiter. Le nombre de produits du catalogue n'en est
 * pas un : c'est un volume, il n'a rien à faire en pastille d'alerte.
 *
 * Silencieux en cas d'échec : la navigation doit s'afficher même quand une
 * ressource est indisponible, simplement sans sa pastille.
 */
export function useSidebarCounters(): Record<string, number> {
	const support = useQuery({
		queryKey: ["sidebar", "support"],
		queryFn: () => supportApi.stats(),
		refetchInterval: 120_000,
		staleTime: 60_000,
		retry: false,
	});

	const shipments = useQuery({
		queryKey: ["sidebar", "shipments"],
		queryFn: () => logisticsApi.shipments({ status: "PENDING", pageSize: 1 }),
		refetchInterval: 120_000,
		staleTime: 60_000,
		retry: false,
	});

	const counters: Record<string, number> = {};
	const ouverts = support.data?.open ?? 0;
	if (ouverts > 0) counters.support = ouverts;
	const aPreparer = shipments.data?.total ?? 0;
	if (aPreparer > 0) counters.logistics = aPreparer;
	return counters;
}
